import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { 
  TwoFactorSetup, 
  EnableTwoFactor, 
  DisableTwoFactor
} from '../../redux/Actions/userStatusService';

export default function SecurityDetails() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Store QR code and secret from API response
  const [secretKey, setSecretKey] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // Get user data from Redux store
  const userInformation = useSelector((state) => state.userStatus.userInformation);
  
  // Fetch user details to check 2FA status
  useEffect(() => {
    if (userInformation && userInformation.isTwoFactorEnabled !== undefined) {
      setTwoFactorEnabled(userInformation.isTwoFactorEnabled);
    }
  }, [userInformation]);
  
  const handleSetup2FA = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Call the TwoFactorSetup API
      const response = await dispatch(TwoFactorSetup());
      
      if (response && response.payload && response.payload.statusCode === 201) {
        // Set the secret key and QR code from the API response
        setSecretKey(response.payload.data.secret);
        setQrCodeUrl(response.payload.data.qrCode);
        setSetupDialogOpen(true);
      } else {
        // Log more details about the response
        console.error("Invalid response format:", response);
        setError('Failed to set up 2FA. Please try again.');
      }
    } catch (err) {
      console.error('Failed to set up 2FA:', err);
      setError(`Failed to set up 2FA: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerify2FA = async () => {
    setLoading(true);
    setError('');
    
    try { 
      // Call the EnableTwoFactor API with the verification code
      const response = await dispatch(EnableTwoFactor({ 
        token: verificationCode,
      }));
      // Check the response from the API
      if (response && response.payload && (response.payload.statusCode === 200 || response.payload.statusCode === 201)) {
        // Update the user's 2FA status in the UI
        setTwoFactorEnabled(true);
        setSuccess('Two-factor authentication has been enabled successfully!');
        setSetupDialogOpen(false);
        
        // Clear any previous error
        setError('');
        
        // Clear the verification code
        setVerificationCode('');
      } else {
        // If the API returned an error message, display it
        const errorMessage = response?.payload?.message || 'Invalid verification code. Please try again.';
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Failed to verify 2FA code:', err);
      setError(`Failed to verify code: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisable2FA = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Call the DisableTwoFactor API
      const response = await dispatch(DisableTwoFactor());
      
      if (response && response.payload && (response.payload.statusCode === 201 || response.payload.statusCode === 204)) {
        setTwoFactorEnabled(false);
        setSuccess('Two-factor authentication has been disabled successfully.');
        setDisableDialogOpen(false);
      } else {
        const errorMessage = response?.payload?.message || 'Failed to disable 2FA. Please try again.';
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Failed to disable 2FA:', err);
      setError(`Failed to disable 2FA: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Paper sx={{ p: 3, boxShadow: 1, borderRadius: 1, bgcolor: 'background.paper', minHeight: '500px' }}>
      <Typography variant="h6" gutterBottom>
        Security Settings
      </Typography>
      
      <Box sx={{ mt: 3 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        {error && !setupDialogOpen && !disableDialogOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1">Two-Factor Authentication (2FA)</Typography>
            <Typography variant="body2" color="text.secondary">
              Add an extra layer of security to your account by requiring a verification code in addition to your password.
            </Typography>
          </Box>
          <Switch
            checked={twoFactorEnabled}
            onChange={() => {
              if (twoFactorEnabled) {
                setDisableDialogOpen(true);
              } else {
                handleSetup2FA();
              }
            }}
            color="primary"
          />
        </Box>
        
        {twoFactorEnabled && (
          <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.background.neutral || '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <VerifiedIcon color="success" sx={{ mr: 1, fontSize: 16 }} />
              Two-factor authentication is currently enabled for your account.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Setup 2FA Dialog */}
      <Dialog open={setupDialogOpen} onClose={() => setSetupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            1. Download an authenticator app like Google Authenticator or Authy.
          </Typography>
          
          <Typography variant="body1" paragraph>
            2. Scan this QR code with your authenticator app:
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            {qrCodeUrl && (
              <img src={qrCodeUrl} alt="QR Code for 2FA" style={{ width: 200, height: 200 }} />
            )}
          </Box>
          
          <Typography variant="body1" paragraph>
            3. If you can't scan the QR code, enter this code manually:
          </Typography>
          
          <Box sx={{ bgcolor: theme.palette.background.neutral || '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
              {secretKey}
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph>
            4. Enter the 6-digit verification code from your authenticator app:
          </Typography>
          
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => {
              // Only allow numeric input
              const value = e.target.value.replace(/[^0-9]/g, '');
              setVerificationCode(value);
            }}
            error={!!error}
            helperText={error || ''}
            sx={{ mt: 1 }}
            inputProps={{ 
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleVerify2FA} 
            variant="contained" 
            disabled={verificationCode.length !== 6 || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Disable 2FA Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)}>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to disable two-factor authentication?
          </Typography>
          
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Warning: Disabling two-factor authentication will make your account less secure.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDisable2FA} 
            variant="contained" 
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
} 